import { get, post } from '@/utils/request'
import { LIVE_ROOM_COME_FROM } from '@/common/constants'

const MODE = {
  VIDEO: 0,
  AUDIO: 1,
}

/**
 * @typedef {Object} AnchorIncome
 * @property {number} coin_count - 金币数量
 * @property {string} describe - 描述
 * @property {string} icon - 图标URL
 */

/**
 * @typedef {Object} RtcServer
 * @property {string} access_token - RTC访问令牌
 * @property {string} channel_id - 频道ID
 * @property {number} bitrate - 视频码率
 * @property {number} enable_h265 - 是否启用H.265：1=是，0=否
 * @property {number} enable_pvc - 是否启用PVC
 * @property {number} enable_sr - 是否启用SR
 * @property {number} hd_switch - 高清开关：1=开启，0=关闭
 * @property {number} audio_scenario - 音频场景类型
 * @property {number} auto_resolution - 自动分辨率
 * @property {number} net_limit - 网络限制
 * @property {string[]} agora_config - Agora配置数组
 * @property {number} device_score_norm - 设备评分
 */

/**
 * @typedef {Object} EmptyPlayInfo
 * @property {string} remind_content - 提醒内容
 * @property {number} remind_duration - 提醒持续时间（秒）
 * @property {number} punishment_duration - 惩罚时长（秒）
 * 当返回 empty_play_info 时，表示检测到用户未露脸，客户端应该显示 remind_content 提醒用户，持续 remind_duration 秒
 */

/**
 * @typedef {Object} VipInfo
 * @property {number} is_vip - VIP等级：-1=非VIP，0=普通用户，1=VIP1，2=VIP2，3=VIP3
 * @property {number} is_new_vip - 是否为新VIP：-1=否，1=是
 * @property {number} is_svip - 是否为超级VIP：-1=否，1=是
 */

/**
 * @typedef {Object} WealthLevel
 * @property {number} level - 财富等级
 * @property {number} charged_coins - 充值金币数
 * @property {number} total_recharge_usd - 总充值美元数
 */

/**
 * @typedef {Object} LiveMember
 * @property {string} id - 加密的用户ID
 * @property {number} member_id - 用户ID
 * @property {string} avatar - 头像URL
 * @property {string} nickname - 昵称
 * @property {number} age - 年龄
 * @property {string} nation - 国家代码
 * @property {number} gender - 性别：1=男，2=女
 * @property {number} role - 角色：1=普通用户，2=主播
 * @property {number} follow_type - 关注关系类型
 * @property {boolean} is_on_mic - 是否在麦上
 * @property {number} cam_status - 摄像头状态：0=关闭，1=开启
 * @property {number} mic_status - 麦克风状态：0=关闭，1=开启
 * @property {string} national_flag - 国旗表情
 * @property {string} label_name - 标签名称
 * @property {Object} [avatar_frame] - 头像框（可选）
 * @property {Object} [enter_bar] - 进场特效（可选）
 * @property {RtcServer} rtc_server - RTC服务器配置对象
 * @property {VipInfo} vip_info - VIP信息
 * @property {WealthLevel} wealth_level - 财富等级
 */

/**
 * @typedef {Object} LoveRoomInfo
 * @property {number} live_id - 直播场次ID（房间唯一标识，每一次开播都会生成一个新的 live_id，是这次直播/连麦的会话标识；用户记录和统计每一场直播的数据；会随着开播、关播而变化）
 * @property {number} room_id - 房间ID（主播的直播间ID，是主播的固定标识，在多人房（派对房、语音房）场景中，room_id 通常等于主播的 member_id；是主播的"直播间"概念，类似于直播间房号；主播每次开播都使用同一个 room_id，相对固定）
 * @property {string} channel_id - RTC频道ID
 * @property {string} chat_room_id - 聊天室ID（IM使用）
 * @property {number} status - 房间状态：1=等待中，2=通话中，3=已结束
 * @property {number} timestamp - 房间创建时间戳（毫秒）
 * @property {number} mode - 通话模式：0=视频，1=语音
 * @property {string} scene_type - 场景类型（如："1v1_video"）
 * @property {Object<number, LiveMember>} live_members - 房间所有成员，key为member_id
 * @property {LiveMember} member - 当前用户的信息
 * @property {number} video_cost - 用户每分钟费用（金币数）
 * @property {number} original_video_cost - 原始费用（未打折前）
 * @property {number} video_income - 主播每分钟收益（金币数）
 * @property {AnchorIncome[]} anchor_income - 主播收益详情列表
 * @property {number} free_duration - 免费时长（秒），0表示无免费时长
 * @property {number} delay_cost_remind - 延迟扣费提醒：1=显示提醒，0=不显示（当为1，并且 free_duration > 0 时，用户有免费时长，客户端应该在免费时长结束前提示用户即将开始扣费）
 * @property {number} countdown - 倒计时秒数
 * @property {number} connect_timeout - 连接超时时间（秒）
 * @property {number} turn_to_private_sec - 转私密通话秒数
 * @property {number} download_video_duration - 下载视频时长
 * @property {number} join_directly - 直接加入：1=直接进入通话，0=需要接听
 * @property {number} forbid_anchor_hang_up_switch - 禁止主播挂断开关
 * @property {number} go_live_alert_sound - 开播提示音
 * @property {number} silence_notice - 静音通知
 * @property {number} abnormal_exit - 异常退出标记
 * @property {number} anchor_live - 主播直播状态
 * @property {string} notice - 房间通知文本
 * @property {Object} [video_guidance] - 视频引导信息（可选）
 * @property {EmptyPlayInfo} [empty_play_info] - 空播信息/人脸检测提醒（可选）
 * @property {Object} [tips] - 提示弹窗信息（定向呼叫返回）
 * @property {Object} [busy_notice] - 忙线提示信息（定向呼叫返回）
 */

/**
 * @typedef {Object} StartCallParams
 * @property {string} [target_id] - 目标用户ID（定向呼叫时必填，随机匹配时不传）
 * @property {number} [count] - 匹配数量（随机匹配时使用，默认1）
 * @property {number} mode - 通话模式：0=视频，1=语音
 * @property {number} action - 操作类型：0=开始匹配/拨打，1=取消匹配/拨打
 * @property {number} [action_type] - 取消类型（仅当action=1时有效）：0=正常取消，1=超时取消，2=异常结束，3=服务器超时
 * @property {number} timestamp - 时间戳（毫秒）
 * @property {string} trace_id - 追踪ID，用于关联整个视频生命周期
 * @property {number} [come_from] - 来源标识，详见 LIVE_ROOM_COME_FROM 常量
 * @property {string} [safe_mode] - 安全模式：'on'=开启，'off'=关闭
 */

/**
 * @typedef {Object} StartCallResponse
 * @property {number} code - 响应码，0表示成功
 * @property {string} error - 错误信息，成功时为空字符串
 * @property {LoveRoomInfo} data - 房间信息
 */

/**
 * 发起/取消视频or语音通话请求，调用者是发起呼叫的人
 * 1. 等待超时的时候发起方要调用该接口取消呼叫
 * 2. 接收方等待超时的时候只关闭弹窗，什么都不用做，也不需要调用接口
 *
 * 支持两种模式：
 * 1. 随机匹配：不传 target_id，系统自动匹配合适的用户
 * 2. 定向呼叫：传入 target_id，直接呼叫指定用户
 *
 * 错误码说明：
 * - 10004：金币不足，需要引导用户充值
 * - 510001：主播忙线
 * - 31001：亲密度不足
 * - 70001/500100：空播惩罚，主播之前空播被系统惩罚
 * - 70002：拒接惩罚，主播频繁拒接被系统惩罚
 * - 10015：需要实名认证
 * - 10020：系统禁言
 * - 其他错误码：一般性业务错误，直接展示 error 字段内容
 *
 * 业务逻辑说明：
 * - 延迟扣费提醒：当 delay_cost_remind=1 且 free_duration>0 时，用户有免费时长，需在免费时长结束前提示即将开始扣费
 * - 人脸检测：如果返回 empty_play_info，说明检测到用户未露脸，需显示提醒
 * - 房间成员：live_members 是 map 结构，key 为 member_id，可通过对比当前用户ID区分自己和对方
 * - RTC 连接：使用 rtc_server.access_token 和 rtc_server.channel_id 初始化 RTC SDK
 *
 * @param {StartCallParams} params - 请求参数
 * @returns {Promise<StartCallResponse>} 通话请求响应
 */
export function loveRoomStart(params, config) {
  return post('/live/v1/love_room/start', params, config)
}
Object.assign(loveRoomStart, {
  MODE,
  SAFE_MODE: {
    ON: 'on',
    OFF: 'off',
  },
  LIVE_ROOM_COME_FROM,
  ACTION: {
    START: 0, // 开始匹配/拨打
    CANCEL: 1, // 取消匹配/拨打
  },
  ACTION_TYPE: { // 只在 ACTION 为 CANCEL 时有效
    DEFAULT: 0, // 正常取消（用户主动）
    TIMEOUT: 1, // 超时取消（对方未接听）
    ABNORMAL_END: 2, // 异常结束（网络错误、页面销毁等）
    TIMEOUT_BY_SERVER: 3, // 超时取消（服务器主动）
  },
  JOIN_DIRECTLY: {
    YES: 1,
    NO: 0,
  },
})

/**
 * @typedef {Object} OperationParams
 * @property {string} target_id - 邀请人的用户ID（必填）
 * @property {number} [action] - 操作类型：0=接受邀请（默认），1=拒绝邀请
 * @property {number} [mode] - 通话模式：0=视频，1=语音（来自IM消息的mode字段）
 * @property {number} [source] - 来源：0=随机匹配，1=定向呼叫
 * @property {number} [come_from] - 视频聊天来源标识
 * @property {number} [target_gender] - 邀请人的性别取向，用于扣费
 * @property {string} [trace_id] - 追踪ID，关联整个视频生命周期
 * @property {number} [live_id] - 场次ID（如果是重新加入）
 * @property {string} [end_reason] - 挂断原因
 * @property {string} [endReason] - iOS挂断原因（会被转换为end_reason="live_limit"）
 */

/**
 * @typedef {Object} OperationSuccessResponse
 * @property {number} code - 响应码，0表示成功
 * @property {string} error - 错误信息，成功时为空字符串
 * @property {Object} data - 响应数据
 * @property {LoveRoomInfo} [data] - 房间信息（接受邀请时返回）
 */

/**
 * @typedef {Object} OperationErrorResponse
 * @property {number} code - 错误码，500100表示业务错误
 * @property {string} error - 错误提示信息（已翻译，可直接展示给用户）
 */

/**
 * 接受或拒绝视频/语音通话邀请
 * ‼️调用者是收到邀请的人，不是发起呼叫的人，超时的时候什么都不用做，不用调用该接口，只关闭弹窗即可
 *
 * 使用场景：
 * 1. 用户收到定向呼叫的IM消息
 * 2. 点击接受按钮：调用此接口 action=0
 * 3. 点击拒绝按钮：调用此接口 action=1
 *
 * 注意事项：
 * - target_id、mode、source、target_gender 等参数应从IM消息中获取
 * - trace_id 用于追踪整个通话流程，应从邀请消息中获取或生成
 * - iOS端的 endReason 字段会被后端转换为 end_reason="live_limit"
 *
 * @param {OperationParams} params - 请求参数
 * @returns {Promise<OperationSuccessResponse|OperationErrorResponse>} 操作响应
 */
export function handleVideoCallInvitation(params, config) {
  return post('/live/v1/love_room/operation', params, config)
}

Object.assign(handleVideoCallInvitation, {
  MODE,
  ACTION: {
    ACCEPT: 0,
    REJECT: 1,
  },
  END_REASON: {
    LIVE_LIMIT: 'live_limit',
    USER_HANGUP: 'user_hangup',
    TIMEOUT: 'timeout',
    NETWORK_ERROR: 'network_error',
    OTHER_SIDE_HANGUP: 'other_side_hangup',
  },
})
/**
 * @typedef {Object} HangUpParams
 * @property {number} live_id - 场次ID/直播ID（必填）
 * @property {number} room_id - 房间ID（必填）
 * @property {string} [end_reason] - 挂断原因，用于统计分析
 *   常见值：
 *   - "user_hangup": 用户主动挂断
 *   - "timeout": 超时无人接听
 *   - "network_error": 网络错误
 *   - "other_side_hangup": 对方挂断
 *   - "kicked_out": 被踢出房间
 * @property {string} [trace_id] - 追踪ID，关联整个视频生命周期
 */

/**
 * @typedef {Object} HangUpResponse
 * @property {number} code - 响应码，0表示成功
 * @property {string} error - 错误信息，成功时为空字符串
 * @property {Object} [data] - 响应数据
 * @property {number} [data.duration] - 通话时长（秒）
 * @property {number} [data.coins_consumed] - 消耗的金币数
 * @property {Object} [data.statistics] - 通话统计信息
 */

/**
 * 挂断视频/语音通话
 * ‼️ 结束正在进行的通话，调用时机是通话建立后，双方都可以调用该接口
 *
 * 注意事项：
 * - 必须传入 live_id 和 room_id，从通话开始时的响应中获取
 * - end_reason 用于统计分析，建议根据实际场景传入准确的原因
 * - 调用此接口后应立即清理本地通话资源（关闭摄像头、麦克风等）
 *
 * @param {HangUpParams} params - 请求参数
 * @returns {Promise<HangUpResponse>} 挂断响应
 */
export function hangUpVideoCall(params, config) {
  return post('/live/v1/love_room/hang_up', params, config)
}

Object.assign(hangUpVideoCall, {
  END_REASON: {
    USER_HANGUP: 'user_hangup',
    TIMEOUT: 'timeout',
    NETWORK_ERROR: 'network_error',
    OTHER_SIDE_HANGUP: 'other_side_hangup',
    KICKED_OUT: 'kicked_out',
  },
})

/**
 * @typedef {Object} RoomInfoParams
 * @property {number} room_id - 房间ID（必填）
 * @property {number} live_id - 直播ID/场次ID（必填）
 * @property {number} [scene] - 场景标识，用于区分不同业务场景
 *   常见值：
 *   - 1: 正常通话场景
 *   - 2: 断线重连场景
 *   - 3: 旁路查询场景
 */

/**
 * @typedef {Object} RoomInfoResponse
 * @property {number} code - 响应码，0表示成功
 * @property {string} error - 错误信息，成功时为空字符串
 * @property {Object} data - 响应数据
 * @property {LoveRoomInfo} data - 房间详细信息
 * @property {number} data.call_duration - 当前通话时长（秒）
 * @property {number} data.coins_per_minute - 每分钟消耗金币
 * @property {number} data.remaining_coins - 剩余金币
 * @property {boolean} data.is_active - 房间是否活跃
 * @property {Object} [data.peer_info] - 对方当前状态信息
 * @property {boolean} data.peer_info.is_online - 对方是否在线
 * @property {string} data.peer_info.connection_state - 对方连接状态
 */

/**
 * 获取视频/语音房间信息
 * 用于查询房间当前状态、通话时长、费用等信息
 *
 * 使用场景：
 * 1. 断线重连：网络恢复后查询房间状态
 * 2. 页面恢复：从后台切换回前台时同步房间信息
 * 3. 轮询更新：定期查询金币余额和通话时长
 * 4. 异常处理：检查房间是否仍然有效
 *
 * 注意事项：
 * - room_id 和 live_id 必须与通话开始时保持一致
 * - 建议在网络恢复、页面可见性变化时调用
 * - 可以根据 is_active 判断房间是否已经结束
 *
 * @param {RoomInfoParams} params - 请求参数
 * @returns {Promise<RoomInfoResponse>} 房间信息响应
 */
export function getVideoRoomInfo(params) {
  return get('/live/v1/love_room/info', params, {
    hideLoading: true, // 静默查询
  })
}

/**
 * @typedef {Object} ReportFirstFrameParams
 * @property {string} live_id - 房间ID（live_id）
 * @property {number} scene - 上报场景：0=服务端上报首帧，1=客户端上报首帧
 * @property {number} first_frame_time - 首帧时间：
 *   - scene=0：Unix 时间戳（秒）
 *   - scene=1：从本地加入频道到首帧出现的时长（秒）
 * @property {string} [target_id] - 对方用户ID（加密），当 scene=1 时建议传入，否则服务端会从数据库查询
 */

/**
 * @typedef {Object} ReportFirstFrameResponse
 * @property {number} code - 状态码：0=成功，非0=失败
 * @property {string} error - 错误信息，成功时为 "success"
 */

/**
 * 上报视频通话首帧时间
 * 用于统计和监控视频通话的首帧加载性能，
 * ‼️发起视频/接听视频时必须上报，否则会被踢出房间
 *
 * 使用场景：
 * 客户端上报：由客户端在视频通话中首帧渲染后调用（scene=1），传入从加入频道到首帧的耗时
 *
 * 场景说明：
 * - scene=0（服务端上报）：
 *   - first_frame_time 为 Unix 时间戳（秒），如：1704787800.5
 *   - 服务端根据 Agora NCS 回调自动上报
 *
 * - scene=1（客户端上报）：
 *   - first_frame_time 为时长（秒），如：2.5 表示 2.5 秒
 *   - 从调用 joinChannel 到远端首帧渲染的时长
 *   - 建议传入 target_id 以提高查询效率
 *
 * 注意事项：
 * - live_id 必须与当前通话的 live_id 一致
 * - 客户端上报时，应在 Agora SDK 的 onFirstRemoteVideoFrame 回调中调用
 * - 首帧时间用于性能监控，建议准确上报
 *
 * @param {ReportFirstFrameParams} params - 请求参数
 * @param {Object} [config] - 请求配置
 * @returns {Promise<ReportFirstFrameResponse>} 上报响应
 */
export function reportFirstFrameTime(params, config) {
  return post('/live/v1/love_room/report_first_frame', {
    scene: 1,
    ...params,
  }, {
    hideLoading: true, // 静默上报，不显示 loading
    ...config,
  })
}

// 场景常量
Object.assign(reportFirstFrameTime, {
  SCENE: {
    SERVER: 0, // 服务端上报
    CLIENT: 1, // 客户端上报
  },
})

/**
 * @typedef {Object} SendLiveRoomMessageParams
 * @property {number} live_id - 直播间ID（必填）
 * @property {string} target_id - 对方用户ID（加密，必填）
 * @property {string} message - 消息内容（必填）
 */

/**
 * 发送直播间消息
 * 用于在1v1视频通话过程中发送文字消息
 *
 * 注意事项：
 * - 消息通过 IM 的 LOVE_ROOM_MESSAGE 类型推送给对方
 * - 不需要会话ID，只需要 live_id 和 target_id
 * - 消息不保存到 IndexedDB，只在内存中显示
 *
 * @param {SendLiveRoomMessageParams} params - 请求参数
 * @returns {Promise<null>} 发送响应
 */
export function sendLiveRoomMessage(params, config = {}) {
  return post('/live/v1/love_room/message', params, {
    hideLoading: true, // 静默发送
    ...config,
  })
}

/**
 * @typedef {Object} LiveRuleParams
 * @property {string} [action] - 操作类型
 * @property {string} [safe_mode] - 安全模式
 */

/**
 * @typedef {Object} LiveRule
 * @property {string} match_both - 匹配both的价格（已下线，默认扣费）
 * @property {string} match_female - 匹配女性的价格（金币）
 * @property {string} match_male - 匹配男性的价格（金币）
 */

/**
 * @typedef {Object} LiveRuleResponse
 * @property {number} my_coin - 用户当前金币余额
 * @property {number} my_basic_coin - 用户基础金币（lado等应用使用）
 * @property {LiveRule} live_rule - 匹配规则
 * @property {boolean} lead_match - 是否引导随机匹配（3日内新用户且未匹配过）
 */

/**
 * 获取视频匹配规则
 * 用于获取视频匹配的价格、用户资产、是否引导随机匹配等信息
 *
 * 使用场景：
 * 1. 进入随机匹配页面前，查询匹配价格和用户金币余额
 * 2. 获取等待页面的提示语（action='wait_invite_intros'）
 * 3. 获取视频后的提示语（action='start_live_intros'）
 *
 * 注意事项：
 * - action 为空或不传时，返回匹配规则和用户资产信息
 * - lead_match 为 true 时，建议引导新用户进行随机匹配
 * - safe_mode 参数用于区分安全模式和付费模式的匹配规则
 *
 * @param {LiveRuleParams} [params] - 请求参数
 * @param {Object} [config] - 请求配置
 * @returns {Promise<LiveRuleResponse>} 匹配规则响应
 */
export function getLiveRule(params = {}, config = {}) {
  return get('/live/v1/love_room/live_rule', params, {
    hideLoading: true, // 静默查询
    ...config,
  })
}

// 接口常量
Object.assign(getLiveRule, {
  ACTION: {
    WAIT_INVITE_INTROS: 'wait_invite_intros', // 等待页面提示语
    START_LIVE_INTROS: 'start_live_intros',   // 视频后提示语
  },
  SAFE_MODE: {
    ON: 'on',   // 安全模式
    OFF: 'off', // 付费模式
  },
})

/**
 * @typedef {Object} MatchPopReportParams
 * @property {string} [target_id] - 主播/对方ID（加密或可解析为int64）。获取弹窗时可空，其他场景必填
 * @property {number} status - 弹窗状态：0=获取弹窗，1=曝光，2=未曝光，3=关闭/拒绝，4=假视频播放失败，5=假视频播放成功
 * @property {number} [scene] - 场景：0=默认，1=回复代发消息，2=新用户
 * @property {number} [action] - 用户操作：1=同意，2=拒绝，3=未应答
 * @property {number} [pop_id] - 弹窗ID（与target_id配合更新对应弹窗状态）
 * @property {string} [reason] - 未弹原因等备注
 */

/**
 * @typedef {Object} MatchPopReportResponse
 * @property {number} code - 响应码，0表示成功
 * @property {string} error - 错误信息，成功时为空字符串
 * @property {Object} [data] - 响应数据
 */

/**
 * 视频弹窗上报
 * 用于上报视频弹窗的各种状态（获取弹窗、曝光、未曝光、同意、拒绝等）
 *
 * 使用场景：
 * 1. 获取弹窗（status=0）：调用接口获取待展示的弹窗信息
 * 2. 弹窗曝光（status=1）：弹窗成功展示时上报
 * 3. 弹窗未曝光（status=2）：弹窗未能展示时上报（如被其他弹窗覆盖）
 * 4. 弹窗关闭/拒绝（status=3）：用户拒绝或关闭弹窗时上报
 * 5. 假视频播放失败（status=4）：假视频播放出错时上报
 * 6. 假视频播放成功（status=5）：假视频播放成功时上报
 *
 * 注意事项：
 * - target_id：获取弹窗时可空，其他场景建议传入
 * - pop_id：用于标识具体的弹窗，建议从获取弹窗接口返回值中获取
 * - action：用于标识用户的具体操作（同意/拒绝/未应答）
 * - 需要 JWT 认证，member_id 从 token 中获取
 *
 * @param {MatchPopReportParams} params - 请求参数
 * @param {Object} [config] - 请求配置
 * @returns {Promise<MatchPopReportResponse>} 上报响应
 */
export function matchPopReport(params, config = {}) {
  return post('/live/v1/love_room/match_pop', params, {
    hideLoading: true, // 静默上报
    hideError: true, // 上报失败不显示错误提示
    ...config,
  })
}

// 接口常量
Object.assign(matchPopReport, {
  STATUS: {
    START: 0,        // 获取弹窗
    EXPOSE: 1,       // 弹窗曝光
    NOT_EXPOSE: 2,   // 弹窗未曝光
    REFUSE: 3,       // 弹窗关闭/拒绝
    VIDEO_ERR: 4,    // 假视频播放失败
    VIDEO_PLAY: 5,   // 假视频播放成功
  },
  ACTION: {
    DEFAULT: 0,      // 默认
    AGREE: 1,        // 同意
    REFUSE: 2,       // 拒绝
    NO_ANSWER: 3,    // 未应答
  },
  SCENE: {
    DEFAULT: 0,      // 默认
    REPLY_MESSAGE: 1,// 回复代发消息
    NEW_USER: 2,     // 新用户
  },
})
